/**
 * Guards the post-login `?redirectTo=` value against open-redirect payloads
 * before `LoginPage` hands it to `navigate()`.
 *
 * `RequireAuth` sets `redirectTo` from the app's OWN current
 * `location.pathname + location.search` (see `components/RequireAuth.tsx`),
 * so the legitimate values are always app-relative paths. But the query
 * param itself is attacker-controllable — anyone can hand an agent a link
 * like `/login?redirectTo=https://evil.example/phish` (or the scheme-relative
 * `//evil.example`) and, pre-fix, `LoginPage` read it straight off the URL
 * with no origin check. A successful login would then hand the browser —
 * with the fresh session context still warm — to an attacker-controlled
 * origin.
 *
 * Only a same-origin, app-relative path is accepted. The value must:
 *   - start with a single "/" — this alone rules out an absolute URL with a
 *     scheme (`https://…`, `javascript:…`): per RFC 3986 a URI scheme can
 *     only appear before the first "/", so anything that reaches this
 *     function with a leading "/" is already guaranteed scheme-free;
 *   - NOT start with "//" or "/\" — both are "scheme-relative": the WHATWG
 *     URL spec treats "\" the same as "/" for the http(s) schemes, so
 *     "/\evil.com" and "//evil.com" both resolve to `evil.com`, not this
 *     origin;
 *   - contain no control characters — besides being invalid in a path,
 *     browsers strip \t \n \r out of a URL before parsing it, so e.g.
 *     "/\t/evil.com" would otherwise smuggle a "//" past the check above;
 *   - still pass all of the above after a SINGLE `decodeURIComponent` pass,
 *     so percent-encoded variants ("/%2F%2Fevil.com", "/%5Cevil.com") don't
 *     sneak through unexamined. (`URLSearchParams`, which `LoginPage` reads
 *     `redirectTo` through, already performs one decode itself, so this
 *     second internal decode also catches a doubly-encoded payload
 *     delivered through the real query string.)
 *
 * Anything that fails any of the above — including a missing or empty value
 * — falls back to `DEFAULT_REDIRECT_TARGET`.
 */

/** Where a missing/invalid `redirectTo` sends the agent instead. */
export const DEFAULT_REDIRECT_TARGET = '/';

// Matches C0 controls (\x00-\x1f) and DEL (\x7f). Intentional — detecting
// control characters (including \t \n \r, which browsers strip from a URL
// before parsing it) is the entire point of this regex.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x1f\x7f]/;

/** True when `value` has the literal shape of a safe, app-relative path. */
function isSafeRedirectShape(value: string): boolean {
  if (CONTROL_CHARS.test(value)) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//') || value.startsWith('/\\')) return false;
  return true;
}

/**
 * Validates a `redirectTo` query value (as returned by
 * `URLSearchParams#get`, so `null` when the param is absent) and returns a
 * safe navigation target: `raw` itself when it's a same-origin app-relative
 * path, otherwise `DEFAULT_REDIRECT_TARGET`.
 */
export function sanitizeRedirectTarget(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_REDIRECT_TARGET;
  if (!isSafeRedirectShape(raw)) return DEFAULT_REDIRECT_TARGET;

  let decodedOnce: string;
  try {
    decodedOnce = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding (e.g. a lone trailing "%") — reject rather
    // than let it slip through unexamined.
    return DEFAULT_REDIRECT_TARGET;
  }
  if (!isSafeRedirectShape(decodedOnce)) return DEFAULT_REDIRECT_TARGET;

  return raw;
}
